import type { ICellRendererParams } from 'ag-grid-community';
import { Button } from 'react-bootstrap';
import type { NavigateFunction } from 'react-router-dom';
import SubtleBadge from '@/components/common/SubtleBadge';
import type { UrlInventoryRow } from '../api/types';
import {
  bucketLabel,
  type InspectCoverageBucket,
} from '../lib/inspect-detail';
import { bucketStatusLabel } from '../lib/inspect-job-diff';
import { inspectDetailToSnapshot } from '../lib/seo-ai-prompt';
import {
  copyText,
  decodeUrlForDisplay,
  formatFaNumber,
  formatTime,
} from '../lib/utils';
import { navigateToIndexWithPrefill, navigateToInspectWithPrefill } from '../lib/url-list-prefill';
import { ActionTip } from '../components/ActionTip';
import { CoverageHelpTip } from '../components/CoverageHelpTip';
import { IndexerIcon } from '../components/IndexerIcon';
import { InspectScheduleNotifyAction } from '../components/InspectScheduleNotifyAction';
import { SearchConsoleIcon } from '../components/SearchConsoleIcon';
import { SeoAiAdviceAction } from '../components/SeoAiAdvice';
import { StaleCrawlBadge } from '../lib/stale-crawl';

function bucketDisplay(bucket: string) {
  if (bucket === 'unknown') return 'نامشخص';
  if (bucket === 'excluded') return bucketLabel('excluded' as InspectCoverageBucket);
  return bucketLabel(bucket as InspectCoverageBucket);
}

function bucketSubtleTone(
  bucket: string,
): 'success' | 'warning' | 'danger' | 'secondary' {
  if (bucket === 'indexed') return 'success';
  if (bucket === 'excluded') return 'warning';
  if (bucket === 'error') return 'danger';
  return 'secondary';
}

export function hasRecentStatusChange(row: UrlInventoryRow) {
  return (
    !!row.lastBucketChangedAt &&
    !!row.lastInspectAt &&
    row.lastBucketChangedAt === row.lastInspectAt &&
    row.previousIndexBucket != null
  );
}

function rowToSnapshot(row: UrlInventoryRow) {
  return inspectDetailToSnapshot({
    url: row.url,
    success: row.lastInspectSuccess ?? false,
    bucket:
      row.indexBucket === 'unknown'
        ? 'error'
        : (row.indexBucket as InspectCoverageBucket),
    verdict: row.verdict ?? undefined,
    coverageState: row.coverageState ?? undefined,
    lastCrawlTime: row.lastCrawlTime ?? undefined,
    inspectLink: row.inspectLink ?? undefined,
    crawlVsLastmod: 'unknown',
    referringUrls: [],
    sitemaps: [],
  });
}

export type UrlInventoryColumnCallbacks = {
  navigate: NavigateFunction;
  onDetail: (url: string) => void;
};

export function urlInventoryColumnConfig({
  navigate,
  onDetail,
}: UrlInventoryColumnCallbacks) {
  return [
    {
      field: 'url',
      title: 'آدرس',
      type: 'string',
      flex: 2.8,
      minWidth: 420,
      searchable: true,
      cellClass: 'cursor-pointer',
      cellRenderer: (params: ICellRendererParams<UrlInventoryRow>) => {
        const row = params.data;
        if (!row) return null;
        const decoded = decodeUrlForDisplay(row.url);
        return (
          <button
            type="button"
            className="btn btn-link btn-sm p-0 text-decoration-none text-start d-flex flex-column justify-content-center min-w-0 w-100 h-100 py-1"
            title={decoded}
            onClick={(event) => {
              event.stopPropagation();
              onDetail(row.url);
            }}
          >
            <span className="mono if-url-clip text-primary">{decoded}</span>
            <span className="text-secondary small text-truncate">
              {formatFaNumber(row.inspectCount)} بازرسی ·{' '}
              {formatFaNumber(row.indexCount)} ایندکس
            </span>
          </button>
        );
      },
    },
    {
      field: 'indexBucket',
      title: 'وضعیت ایندکس',
      type: 'string',
      width: 150,
      searchable: true,
      cellRenderer: (params: ICellRendererParams<UrlInventoryRow>) => {
        const row = params.data;
        if (!row) return null;
        return (
          <span className="d-inline-flex align-items-center py-1">
            <SubtleBadge
              bg={bucketSubtleTone(row.indexBucket)}
              pill
              className="fs--2"
              style={{ lineHeight: 1.35 }}
            >
              {bucketDisplay(row.indexBucket)}
            </SubtleBadge>
          </span>
        );
      },
    },
    {
      field: 'lastBucketChangedAt',
      title: 'تغییر',
      type: 'string',
      width: 160,
      searchable: false,
      cellRenderer: (params: ICellRendererParams<UrlInventoryRow>) => {
        const row = params.data;
        if (!row || !hasRecentStatusChange(row)) return '—';
        return (
          <span className="small">
            {bucketStatusLabel(
              row.previousIndexBucket === 'unknown'
                ? null
                : row.previousIndexBucket,
            )}
            <span className="mx-1" aria-hidden>
              →
            </span>
            {bucketStatusLabel(
              row.indexBucket === 'unknown' ? null : row.indexBucket,
            )}
          </span>
        );
      },
    },
    {
      field: 'coverageState',
      title: 'پوشش',
      type: 'string',
      flex: 1,
      minWidth: 260,
      searchable: true,
      cellClass: 'd-flex align-items-center',
      cellRenderer: (params: ICellRendererParams<UrlInventoryRow>) => {
        const coverage = params.data?.coverageState;
        if (!coverage) return '—';
        return (
          <span className="d-inline-flex align-items-center flex-nowrap gap-1 mw-100 min-w-0">
            <span className="text-truncate min-w-0" title={coverage}>
              {coverage}
            </span>
            <CoverageHelpTip coverageState={coverage} />
          </span>
        );
      },
    },
    {
      field: 'lastCrawlTime',
      title: 'تاریخ خزش گوگل',
      type: 'string',
      width: 220,
      minWidth: 180,
      searchable: false,
      cellClass: 'd-flex align-items-center',
      cellRenderer: (params: ICellRendererParams<UrlInventoryRow>) => {
        const lastCrawlTime = params.data?.lastCrawlTime;
        const staleCrawl = params.data?.staleCrawl;
        if (!lastCrawlTime && !staleCrawl) return '—';
        return (
          <span className="d-inline-flex align-items-center flex-nowrap gap-1 mw-100 min-w-0">
            {lastCrawlTime ? (
              <span className="text-nowrap">{formatTime(lastCrawlTime)}</span>
            ) : (
              <span className="text-secondary">—</span>
            )}
            {staleCrawl ? <StaleCrawlBadge staleCrawl={staleCrawl} /> : null}
          </span>
        );
      },
    },
    {
      field: 'lastIndexAt',
      title: 'آخرین ایندکس',
      type: 'string',
      width: 180,
      minWidth: 160,
      searchable: false,
      cellRenderer: (params: ICellRendererParams<UrlInventoryRow>) => {
        const row = params.data;
        if (!row?.lastIndexAt) return '—';
        return (
          <span className="d-inline-flex align-items-center gap-1 flex-wrap py-1">
            <span className="text-nowrap">{formatTime(row.lastIndexAt)}</span>
            <SubtleBadge
              bg={row.lastIndexSuccess ? 'success' : 'danger'}
              pill
              className="fs--2"
              style={{ lineHeight: 1.35 }}
            >
              {row.lastIndexSuccess ? 'موفق' : 'ناموفق'}
            </SubtleBadge>
          </span>
        );
      },
    },
    {
      field: 'sitemapLastmod',
      title: 'lastmod',
      type: 'string',
      width: 140,
      searchable: false,
      cellRenderer: (params: ICellRendererParams<UrlInventoryRow>) =>
        params.data?.sitemapLastmod
          ? formatTime(params.data.sitemapLastmod)
          : '—',
    },
    {
      field: '_actions',
      title: 'عملیات',
      width: 220,
      maxWidth: 240,
      sortable: false,
      pinned: 'left',
      lockPosition: true,
      suppressMovable: true,
      suppressHeaderMenuButton: true,
      headerClass: 'text-center',
      cellClass: 'd-flex align-items-center justify-content-center',
      cellRenderer: (params: ICellRendererParams<UrlInventoryRow>) => {
        const row = params.data;
        if (!row) return null;
        const decoded = decodeUrlForDisplay(row.url);
        return (
          <div className="if-table-actions justify-content-center">
            <ActionTip tip="جزئیات + تاریخچه">
              <Button
                size="sm"
                variant="link"
                className="if-table-action"
                aria-label="جزئیات + تاریخچه"
                onClick={() => onDetail(row.url)}
              >
                <IndexerIcon name="clock-history" />
              </Button>
            </ActionTip>
            <SeoAiAdviceAction
              url={row.url}
              snapshots={[rowToSnapshot(row)]}
              disabled={row.indexBucket === 'indexed'}
            />
            {row.inspectLink ? (
              <ActionTip tip="باز کردن در Search Console">
                <Button
                  size="sm"
                  variant="outline-secondary"
                  className="if-table-action if-table-action--brand"
                  aria-label="باز کردن در Search Console"
                  href={row.inspectLink}
                  target="_blank"
                  rel="noreferrer"
                >
                  <SearchConsoleIcon />
                </Button>
              </ActionTip>
            ) : null}
            <ActionTip tip="درخواست ایندکس">
              <Button
                size="sm"
                variant="link"
                className="if-table-action"
                aria-label="درخواست ایندکس"
                onClick={() =>
                  navigateToIndexWithPrefill(navigate, [row.url], 'url-list')
                }
              >
                <IndexerIcon name="card-text" />
              </Button>
            </ActionTip>
            <ActionTip tip="بازرسی">
              <Button
                size="sm"
                variant="link"
                className="if-table-action"
                aria-label="بازرسی"
                onClick={() =>
                  navigateToInspectWithPrefill(navigate, [row.url], 'url-list')
                }
              >
                <IndexerIcon name="search" />
              </Button>
            </ActionTip>
            <InspectScheduleNotifyAction url={row.url} source="url-list" />
            <ActionTip tip="کپی">
              <Button
                size="sm"
                variant="link"
                className="if-table-action"
                aria-label="کپی"
                onClick={() => void copyText(decoded)}
              >
                <IndexerIcon name="copy" />
              </Button>
            </ActionTip>
          </div>
        );
      },
    },
  ];
}
